import { forwardRef, useMemo, useState, type ReactNode } from 'react'
import { Button, Card, Chip, Meter, Table, Tabs } from '@heroui/react'
import type { BranchState } from '../lib/branchState'
import { byCallingOrder } from '../lib/callingWait'
import { secondsSince } from '../lib/clock'
import { clock } from '../lib/deskStatus'
import { findActions } from '../lib/findActions'
import { issuedOn } from '../lib/findResult'
import { currentServiceId } from '../lib/move'
import { DAY_FILTERS, deskAvailability, inFilter, newestFirst, numberParts, queueLetter, ticketMatches, type DayFilter } from '../lib/receptionDesk'
import { deskPill, deskSince, queueRows, type DeskPill, type SlaState } from '../lib/receptionStatus'
import { nameIn, type StaffKey } from '../lib/staffText'
import type { Colleague } from '../lib/transfer'
import type { Ticket, WorkProfile } from '../lib/types'
import { mins } from '../ui/hallParts'
import { Pager, usePaged } from '../ui/Pager'
import { useStaffText } from '../ui/StaffText'
import { TransferredChip } from '../ui/TransferredChip'
import { StatusChip, type ChipColor } from './receptionKit'

export type StatusPanel = 'tickets' | 'queues' | 'desks'
export type Where = { main: string; sub?: string; tone?: string; over?: boolean }

const FILTER_KEY: Record<DayFilter, StaffKey> = {
  all: 'reception.filter.all',
  waiting: 'reception.filter.waiting',
  desk: 'reception.filter.desk',
  done: 'reception.filter.done',
  missed: 'reception.filter.missed',
  over: 'reception.filter.over',
}

const SLA_KEY: Record<SlaState, StaffKey> = {
  idle: 'reception.sla.idle',
  ok: 'reception.sla.ok',
  close: 'reception.sla.close',
  over: 'reception.sla.over',
}
const SLA_TONE: Record<SlaState, string> = { idle: 'ok', ok: 'ok', close: 'warn', over: 'bad' }
const SLA_METER: Record<SlaState, ChipColor> = { idle: 'success', ok: 'success', close: 'warning', over: 'danger' }

const DESK_KEY: Record<DeskPill['kind'], StaffKey> = {
  free: 'reception.desk.free',
  serving: 'reception.desk.servingNo',
  calling: 'reception.desk.callingNo',
  break: 'reception.desk.break',
  closed: 'reception.desk.closed',
}
const DESK_TONE: Record<DeskPill['kind'], string> = { free: 'ok', serving: 'serve', calling: 'call', break: 'warn', closed: 'off' }

/** The queue letter tile. */
export const Qt = ({ children }: { children: ReactNode }) => (
  <span className="rx-qt" aria-hidden="true">
    {children}
  </span>
)

/** The status card under the service tiles: today's tickets, the queues against target, the desks. */
export const ReceptionStatus = forwardRef<HTMLDivElement, {
  panel: StatusPanel
  onPanel: (p: StatusPanel) => void
  tickets: readonly Ticket[]
  state: BranchState | null
  profiles: WorkProfile[] | null
  now: number
  /** The page's Find a ticket box. */
  query: string
  where: (t: Ticket) => Where
  customerOf: (t: Ticket) => { name: string; phone: string }
  serviceName: (id: string) => string
  time: (iso: string | undefined) => string
  businessDate: string | undefined
  canSupervise: boolean
  colleagues: readonly Colleague[]
  onOpen: (id: string, start?: 'move' | 'cancel') => void
}>(function ReceptionStatus(
  { panel, onPanel, tickets, state, profiles, now, query, where, customerOf, serviceName, time, businessDate, canSupervise, colleagues, onOpen },
  ref,
) {
  const { lang, say } = useStaffText()
  const [filter, setFilter] = useState<DayFilter>('all')
  const counters = state?.counters ?? []

  const counts = useMemo(() => {
    const out = {} as Record<DayFilter, number>
    for (const f of DAY_FILTERS) out[f] = tickets.filter((t) => inFilter(t, f, now)).length
    return out
  }, [tickets, now])

  const shown = useMemo(() => {
    const list = tickets.filter((t) => inFilter(t, filter, now) && ticketMatches(t, query))
    // Waiting reads in calling order: "next to be called" off the same list.
    return filter === 'waiting' || filter === 'over' ? byCallingOrder(list, now, state?.bookedFirst ?? true) : list.sort(newestFirst)
  }, [tickets, filter, query, now, state?.bookedFirst])
  const { rows: pageRows, pager } = usePaged(shown, `${filter}|${query}`)

  const queues = useMemo(() => queueRows(state?.queues ?? [], state?.tickets ?? [], counters, profiles, now), [state, counters, profiles, now])
  const anyOver = queues.some((q) => q.sla === 'over')
  const desksOpen = counters.filter((d) => deskAvailability(d) !== 'closed').length
  const letterOf = (t: Ticket) => numberParts(t).letter

  return (
    <Card ref={ref} className="rx-today" aria-label={say('reception.ticketStatus')}>
      <Card.Header className="rx-card-h">
        <Tabs selectedKey={panel} onSelectionChange={(k) => onPanel(String(k) as StatusPanel)} className="rx-tabs">
          <Tabs.ListContainer>
            <Tabs.List aria-label={say('reception.ticketStatus')}>
              <Tabs.Tab id="tickets">
                {say('reception.panel.tickets')}
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="queues">
                {say('reception.panel.queues')}
                {anyOver && <span className="rx-dot" role="img" aria-label={say('reception.panel.overDot')} />}
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="desks">
                {say('reception.desks')}
                <span className="rx-of">{say('reception.panel.desksCount', { n: desksOpen, of: counters.length })}</span>
                <Tabs.Indicator />
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>
      </Card.Header>

      {panel === 'tickets' && (
        <>
          <Tabs variant="secondary" selectedKey={filter} onSelectionChange={(k) => setFilter(String(k) as DayFilter)} className="rx-filters">
            <Tabs.ListContainer>
              <Tabs.List aria-label={say('reception.ticketStatus')}>
                {DAY_FILTERS.map((f) => (
                  <Tabs.Tab key={f} id={f} className={f === 'over' ? 'rx-filter--over' : undefined}>
                    {say(FILTER_KEY[f])}
                    <span className="rx-count">{counts[f]}</span>
                    <Tabs.Indicator />
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs.ListContainer>
          </Tabs>
          {shown.length === 0 ? (
            <p className="rx-empty">{query ? say('reception.noMatch', { q: query }) : say('reception.noTickets')}</p>
          ) : (
            <div>
              <Table variant="secondary" className="rx-table">
                <Table.ScrollContainer>
                  <Table.Content aria-label={say('reception.ticketStatus')} className="rx-tickets">
                    <Table.Header>
                      <Table.Column isRowHeader className="rx-col-ticket">
                        {say('reception.col.ticket')}
                      </Table.Column>
                      <Table.Column className="rx-col-service">{say('reception.col.service')}</Table.Column>
                      <Table.Column className="rx-col-who">{say('reception.col.customer')}</Table.Column>
                      <Table.Column className="rx-col-issued">{say('reception.col.issued')}</Table.Column>
                      <Table.Column className="rx-col-where">{say('reception.col.where')}</Table.Column>
                      <Table.Column className="rx-col-act">
                        <span className="sr">{say('reception.col.actions')}</span>
                      </Table.Column>
                    </Table.Header>
                    <Table.Body>
                      {pageRows.map((t) => {
                        const w = where(t)
                        const letter = letterOf(t)
                        const who = customerOf(t)
                        const other = issuedOn(t, businessDate, lang)
                        const actions = findActions(t, canSupervise)
                        const rowActs = t.state === 'waiting' && (actions.includes('move') || actions.includes('remove'))
                        const off = t.state === 'void' || t.state === 'closed'
                        return (
                          <Table.Row
                            key={t.id}
                            id={t.id}
                            onAction={() => onOpen(t.id)}
                            className={['rx-row', w.over ? 'rx-row--over' : '', off ? 'rx-row--off' : ''].filter(Boolean).join(' ')}
                          >
                            <Table.Cell>
                              <span className="rx-no">{t.displayNumber}</span>
                              {t.priority !== 'standard' && (
                                <Chip size="sm" variant="soft" color="warning" className="rx-mark">
                                  {t.priority === 'vip' ? say('common.vip') : say('common.priority')}
                                </Chip>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <span className="rx-nm">
                                {letter && <Qt>{letter}</Qt>}
                                <span>{serviceName(currentServiceId(t))}</span>
                              </span>
                            </Table.Cell>
                            <Table.Cell>
                              <span className="rx-who">
                                {who.name || (!who.phone && <span className="rx-muted">—</span>)}
                                {who.phone && <span className="rx-sub">{who.phone}</span>}
                              </span>
                            </Table.Cell>
                            <Table.Cell className="rx-time">
                              {time(t.issuedAt)}
                              {other && <span className="rx-sub">{other}</span>}
                            </Table.Cell>
                            <Table.Cell>
                              <span className="rx-where">
                                <span className="rx-marks">
                                  <StatusChip tone={w.tone}>{w.main}</StatusChip>
                                  {(t.state === 'waiting' || t.state === 'parked') && (
                                    <TransferredChip ticket={t} counters={counters} colleagues={colleagues} className="rx-sent" />
                                  )}
                                </span>
                                {w.sub && (
                                  <span className="rx-sub rx-sub--line" title={w.sub}>
                                    {w.sub}
                                  </span>
                                )}
                              </span>
                            </Table.Cell>
                            <Table.Cell className="rx-act">
                              {rowActs && (
                                <span className="rx-act__do">
                                  {actions.includes('move') && (
                                    <Button variant="ghost" size="sm" onPress={() => onOpen(t.id, 'move')}>
                                      {say('reception.transfer')}
                                    </Button>
                                  )}
                                  {actions.includes('remove') && (
                                    <Button variant="ghost" size="sm" className="rx-danger" onPress={() => onOpen(t.id, 'cancel')}>
                                      {say('common.cancel')}
                                    </Button>
                                  )}
                                </span>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        )
                      })}
                    </Table.Body>
                  </Table.Content>
                </Table.ScrollContainer>
              </Table>
              <Pager {...pager} />
            </div>
          )}
        </>
      )}

      {panel === 'queues' &&
        (queues.length === 0 ? (
          <p className="rx-empty">{say('reception.noQueues')}</p>
        ) : (
          <>
            <Table variant="secondary" className="rx-table">
              <Table.ScrollContainer>
                <Table.Content aria-label={say('reception.panel.queues')} className="rx-queues">
                  <Table.Header>
                    <Table.Column isRowHeader>{say('reception.col.queue')}</Table.Column>
                    <Table.Column className="rx-col-n">{say('reception.col.waiting')}</Table.Column>
                    <Table.Column className="rx-col-wait">{say('reception.col.longest')}</Table.Column>
                    <Table.Column className="rx-col-n">{say('reception.col.openDesks')}</Table.Column>
                    <Table.Column className="rx-col-status">{say('reception.col.status')}</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {queues.map((r) => {
                      const n = r.queue.serviceIds?.length ?? 0
                      const facts = [
                        n > 0 ? (n === 1 ? say('reception.queue.service1') : say('reception.queue.services', { n })) : '',
                        r.queue.serviceLevelSec ? say('reception.queue.target', { time: mins(r.queue.serviceLevelSec, say) }) : '',
                      ].filter(Boolean)
                      return (
                        <Table.Row key={r.queue.queueId} id={r.queue.queueId}>
                          <Table.Cell>
                            <span className="rx-nm">
                              <Qt>{queueLetter(r.queue)}</Qt>
                              <span className="rx-nm__text">
                                <strong>{nameIn(r.queue.name, lang, r.queue.code)}</strong>
                                {facts.length > 0 && <span className="rx-sub">{facts.join(' · ')}</span>}
                              </span>
                            </span>
                          </Table.Cell>
                          <Table.Cell className="rx-num">{r.waiting}</Table.Cell>
                          <Table.Cell>
                            <strong className="rx-num">{r.waiting > 0 ? mins(r.longestSec, say) : '—'}</strong>
                            {r.ratio !== null && (
                              <Meter
                                className="rx-meter"
                                size="sm"
                                color={SLA_METER[r.sla]}
                                value={Math.min(100, Math.round(r.ratio * 100))}
                                aria-label={say(SLA_KEY[r.sla])}
                              >
                                <Meter.Track>
                                  <Meter.Fill />
                                </Meter.Track>
                              </Meter>
                            )}
                          </Table.Cell>
                          <Table.Cell className="rx-num">{r.openDesks}</Table.Cell>
                          <Table.Cell>
                            <StatusChip tone={SLA_TONE[r.sla]}>{say(SLA_KEY[r.sla])}</StatusChip>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
            <p className="rx-legend">
              <span data-sla="ok">{say('reception.sla.ok')}</span>
              <span data-sla="close" title={say('reception.sla.closeHint')}>
                {say('reception.sla.close')}
              </span>
              <span data-sla="over">{say('reception.sla.over')}</span>
            </p>
          </>
        ))}

      {panel === 'desks' &&
        (counters.length === 0 ? (
          <p className="rx-empty">{say('reception.noDesks')}</p>
        ) : (
          <Table variant="secondary" className="rx-table">
            <Table.ScrollContainer>
              <Table.Content aria-label={say('reception.desks')} className="rx-desks">
                <Table.Header>
                  <Table.Column className="rx-col-code">{say('reception.col.code')}</Table.Column>
                  <Table.Column isRowHeader>{say('reception.col.desk')}</Table.Column>
                  <Table.Column>{say('reception.col.clerk')}</Table.Column>
                  <Table.Column>{say('reception.col.service')}</Table.Column>
                  <Table.Column className="rx-col-status">{say('reception.col.status')}</Table.Column>
                  <Table.Column className="rx-col-time">{say('reception.col.txTime')}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {counters.map((d) => {
                    const pill = deskPill(d)
                    const closed = pill.kind === 'closed'
                    const since = deskSince(d, state?.tickets ?? [])
                    const current = pill.number ? state?.tickets.find((x) => x.id === d.ticketId) : undefined
                    const service = current ? serviceName(currentServiceId(current)) : ''
                    return (
                      <Table.Row key={d.counterId} id={d.counterId} className={closed ? 'rx-row--off' : undefined}>
                        <Table.Cell className="rx-muted rx-num">{d.code}</Table.Cell>
                        <Table.Cell>
                          <strong>{d.name}</strong>
                        </Table.Cell>
                        <Table.Cell>
                          {!closed && d.staffName ? (
                            <span className="rx-who">
                              <span title={d.staffName}>{d.staffName}</span>
                              {d.profileName && <span className="rx-sub">{d.profileName}</span>}
                            </span>
                          ) : (
                            <span className="rx-muted">—</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <span title={service || undefined}>{service}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <StatusChip tone={DESK_TONE[pill.kind]}>
                            {pill.number
                              ? say(DESK_KEY[pill.kind], { number: pill.number })
                              : say(pill.kind === 'serving' ? 'reception.desk.serving' : DESK_KEY[pill.kind])}
                          </StatusChip>
                        </Table.Cell>
                        <Table.Cell className="rx-num">
                          <strong>{since ? clock(secondsSince(since, now)) : ''}</strong>
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        ))}
    </Card>
  )
})
